import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { tableGrid } from '@w/content-blocks/src/table-html'
import { EllipsisVerticalIcon, PlusIcon } from '@w/ui/components/icon'
import { IconButton } from '@w/ui/components/icon-button'
import type { EditorIntent } from '../../core'
import { locate, rows } from '../../core/tree-read'
import type { CoreViewApi, Overlay } from '../view-api'
import { blockElement, findBlock, itemClass, menuKey, OverlayPortal, panelClass, select, useDismiss, usePosition, type OverlayApi } from './shared'

type Control = Extract<EditorIntent, { type: 'tableControl' }>['control']
const controls: [Control, string, string][] = [
  ['header-row', 'header-row', 'Toggle header row'], ['header-column', 'header-column', 'Toggle header column'],
  ['merge', 'merge', 'Merge selected cells'], ['unmerge', 'unmerge', 'Unmerge cell'],
  ['add-row', 'insert-row', 'Insert row below'], ['add-column', 'insert-column', 'Insert column right'],
  ['delete-row', 'delete-row', 'Delete row'], ['delete-column', 'delete-column', 'Delete column'],
]

export const TableControls: Overlay = ({ api }: { api: OverlayApi }) => {
  const [hovered, setHovered] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const menu = useRef<HTMLDivElement>(null)
  const [panel, setPanel] = useState<HTMLDivElement | null>(null)
  const selection = api.state.selection
  const entries = rows(api.state.blocks)
  const row = selection ? entries[locate(entries, selection.focus).row] : null
  const focusedCell = row?.block.type === 'td' ? row.block.id : null
  const id = api.layout === 'touch' ? focusedCell : hovered && findBlock(api.state.blocks, hovered) ? hovered : focusedCell
  const committed = useCommittedBlock(api, id)
  const element = committed?.element ?? null
  const position = usePosition(api, element, false, panel)
  useDismiss(open, () => { setOpen(false) }, menu)
  useEffect(() => {
    if (api.layout === 'touch' || !api.root) return () => {}
    const root = api.root
    const move = (event: MouseEvent) => {
      const cell = event.target instanceof Element ? event.target.closest<HTMLElement>('td,th') : null
      if (cell && root.contains(cell)) setHovered(cell.dataset.blockId ?? null)
      else if (!open && !(event.target instanceof Element && event.target.closest('[data-content-block-table-controls]'))) setHovered(null)
    }
    const clear = () => { if (!open) setHovered(null) }
    document.addEventListener('mousemove', move)
    document.addEventListener('scroll', clear, true)
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('scroll', clear, true) }
  }, [api.root, api.layout, open])
  function apply(control: Control) {
    if (!id) return
    const cellRow = entries.find(entry => entry.block.id === id)
    const table = cellRow?.parents.find(parent => parent.type === 'table')
    if (!table || table.type !== 'table') return
    const grid = tableGrid(table)
    const y = grid.findIndex(cells => cells.some(cell => cell.id === id))
    const x = grid[y]?.findIndex(cell => cell.id === id) ?? -1
    if (y < 0 || x < 0) return
    if (!selection?.table || selection.table.tableId !== table.id) {
      const point = { blockId: id, offset: 0 }
      select(api, { anchor: point, focus: point, cell: { tableId: table.id, x, y } })
    }
    api.dispatch({ type: 'tableControl', control })
    setOpen(false)
    api.root?.focus()
  }
  if (api.mode !== 'edit' || !committed || !id || !findBlock(api.state.blocks, id)) return null
  const { rect } = committed
  return <OverlayPortal api={api}><div ref={menu} data-content-block-table-controls="true">
    {api.layout !== 'touch' && <>
      <IconButton aria-label="Add row below" data-testid="content-blocks-editor-table-add-row" size="sm"
        className="border-border bg-surface-1 fixed z-50 size-5 rounded-full shadow-sm"
        style={{ left: rect.left + rect.width / 2 - 10, top: rect.bottom - 10 }}
        onMouseDown={event => { event.preventDefault() }} onClick={() => { apply('add-row') }}><PlusIcon className="size-3" />Add row below</IconButton>
      <IconButton aria-label="Add column right" data-testid="content-blocks-editor-table-add-column" size="sm"
        className="border-border bg-surface-1 fixed z-50 size-5 rounded-full shadow-sm"
        style={{ left: rect.right - 10, top: rect.top + rect.height / 2 - 10 }}
        onMouseDown={event => { event.preventDefault() }} onClick={() => { apply('add-column') }}><PlusIcon className="size-3" />Add column right</IconButton>
    </>}
    <IconButton aria-label="Row and column menu" data-testid="content-blocks-editor-table-cell-menu" size="sm"
      className="border-border bg-surface-1 fixed z-50 size-5 rounded-md shadow-sm"
      style={{ left: rect.right - 24, top: rect.top + 4 }} aria-haspopup="menu" aria-expanded={open}
      onMouseDown={event => { event.preventDefault() }} onClick={() => { setOpen(!open) }}><EllipsisVerticalIcon className="size-3" />Row and column menu</IconButton>
    {open && <div ref={setPanel} role="menu" tabIndex={-1} onKeyDown={menuKey} aria-label="Row and column actions" className={`${panelClass} w-48 overflow-y-auto`} style={position}>
      {controls.map(([control, testId, title]) => <button key={control} type="button" role="menuitem" className={itemClass}
        data-testid={`content-blocks-editor-table-${testId}`} onMouseDown={event => { event.preventDefault() }}
        onClick={() => { apply(control) }}>{title}</button>)}
    </div>}
  </div></OverlayPortal>
}

/** The block's committed element and box, read after commit like `usePosition`; Lexical `TableActionMenuPlugin` reads
 * `editor.getElementByKey` from an update listener. A render-time lookup misses a cell that the same commit creates. */
function useCommittedBlock(api: CoreViewApi, id: string | null) {
  const [committed, setCommitted] = useState<{ element: HTMLElement; rect: DOMRect } | null>(null)
  useLayoutEffect(() => {
    const update = () => {
      const element = id ? blockElement(api, id) : null
      setCommitted(element ? { element, rect: element.getBoundingClientRect() } : null)
    }
    update()
    window.addEventListener('resize', update)
    document.addEventListener('scroll', update, true)
    return () => { window.removeEventListener('resize', update); document.removeEventListener('scroll', update, true) }
  }, [api, id])
  return committed
}
